import mean from 'lodash/mean';

const POLLING_INTERVAL_MS = 2000;
const MIN_SECONDS_FOR_FPS = 1;
const MAX_SAMPLES = 10;

/**
 * Watch an HTML `video` element and try to work out the frame rate.
 * See https://github.com/Daiz/frame-accurate-ish for details.
 */
export class FPSObserver {
  private videoElement: HTMLVideoElement;
  private lastTime = 0;
  private lastFrameCount = 0;
  private interval: number;

  // Average together up to MAX_SAMPLES fps samples to get a more accurate frame rate.
  private fpsSamples: number[] = [];
  private nextSampleIndex = 0;

  /** The observed FPS of the video, or undefined if it is not available. */
  public fps: number | undefined;

  /** Create a new FPSObserver. */
  constructor(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement;
    this.videoElement.addEventListener('seeked', this._handleSeeked);
    this.interval = window.setInterval(this._update, POLLING_INTERVAL_MS);
  }

  destroy() {
    this.videoElement.removeEventListener('seeked', this._handleSeeked);
    clearInterval(this.interval);
  }

  private _handleSeeked = () => {
    // When the user seeks, reset `lastTime` and `lastFrameCount`.
    this.lastTime = this.videoElement.currentTime;
    this.lastFrameCount = getFrameCount(this.videoElement);
  };

  private _update = () => {
    const frameCount = getFrameCount(this.videoElement);
    if (frameCount !== undefined) {
      const newFrames = frameCount - this.lastFrameCount;
      const currentTime = this.videoElement.currentTime;
      const secondsElapsed = currentTime - this.lastTime;

      if (secondsElapsed > MIN_SECONDS_FOR_FPS) {
        // If the user seeks, sometimes the `_handleSeeked` event won't fire for
        // a while in Chrome.  Work out what we expect the `secondsElapsed` to be,
        // and if it's wrong, ignore this sample.
        const expectedElapsed = (POLLING_INTERVAL_MS * this.videoElement.playbackRate) / 1000;

        if (Math.abs(secondsElapsed - expectedElapsed) < 0.1) {
          this._addSample(newFrames / secondsElapsed);
        }
        this.lastFrameCount = frameCount;
        this.lastTime = currentTime;
      }
    }
  };

  private _addSample(fps: number) {
    this.fpsSamples[this.nextSampleIndex++] = fps;
    if (this.nextSampleIndex >= MAX_SAMPLES) {
      this.nextSampleIndex = 0;
    }
    this.fps = mean(this.fpsSamples);
  }
}

/**
 * Get the total number of frames rendered by the video element since it was created.
 */
function getFrameCount(videoElement: HTMLVideoElement) {
  const video = videoElement as any;
  if ('webkitDecodedFrameCount' in video) {
    // Chrome and other Webkit based browsers.
    return video.webkitDecodedFrameCount;
  } else if ('mozPaintedFrames' in video) {
    // Firefox.
    return video.mozPaintedFrames;
  }
  return undefined;
}
